import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  ChevronRight,
  Clock,
  DollarSign,
  Loader2,
  MapPin,
  Star,
  CheckCircle2,
} from 'lucide-react';

import careersService from '../services/careers.service';
import type { PublicJDItem, ParsedJDData } from '../types/careers';

const MAX_SUGGESTIONS = 3;

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const w = Math.floor(diffDays / 7);
    return `${w} ${w === 1 ? 'week' : 'weeks'} ago`;
  }
  const m = Math.floor(diffDays / 30);
  return `${m} ${m === 1 ? 'month' : 'months'} ago`;
}

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function getLocationText(parsed: ParsedJDData | null): string | null {
  if (!parsed?.location) return null;
  const { city, country, remote } = parsed.location;
  if (remote && !city && !country) return 'Remote';
  const parts = [city, country].filter(Boolean).join(', ');
  if (remote && parts) return `${parts} · Remote`;
  return parts || null;
}

function getSalaryText(parsed: ParsedJDData | null): string | null {
  if (!parsed?.salary_range) return null;
  const { text, min_amount, max_amount, currency, period } = parsed.salary_range;
  if (text) return text;
  if (min_amount != null && max_amount != null) {
    const curr = currency || '';
    const per = period ? `/${period}` : '';
    return `${curr}${min_amount} – ${curr}${max_amount}${per}`;
  }
  return null;
}

export function CareersJDDetailPage() {
  const { workspaceCode, jdId } = useParams<{ workspaceCode: string; jdId: string }>();
  const navigate = useNavigate();

  const [jd, setJd] = useState<PublicJDItem | null>(null);
  const [suggestions, setSuggestions] = useState<PublicJDItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceCode || !jdId) return;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [result, careersData] = await Promise.all([
          careersService.getCareerJD(workspaceCode, jdId),
          careersService.getCareers(workspaceCode).catch(() => null),
        ]);
        setJd(result);
        if (careersData) {
          setSuggestions(
            careersData.items.filter((item) => item.id !== jdId).slice(0, MAX_SUGGESTIONS)
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load job');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [workspaceCode, jdId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto" />
          <p className="mt-3 text-gray-400 text-sm">Loading position...</p>
        </div>
      </div>
    );
  }

  if (error || !jd) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
          <h2 className="mt-4 text-lg font-semibold text-gray-900">{error || 'Position not found'}</h2>
          <p className="mt-2 text-gray-400 text-sm">This position may have been removed.</p>
          <button
            onClick={() => navigate(`/${workspaceCode}`)}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to all positions
          </button>
        </div>
      </div>
    );
  }

  const parsed = jd.parsed_data as ParsedJDData | null;
  const title = parsed?.title || jd.name;
  const location = getLocationText(parsed);
  const salary = getSalaryText(parsed);
  const companyName = parsed?.company_info?.name;
  const skills = parsed?.skills_required ?? [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top nav */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate(`/${workspaceCode}`)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            All positions
          </button>
          {companyName && (
            <span className="text-sm font-semibold text-gray-700">{companyName}</span>
          )}
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">{title}</h1>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">
            {location && (
              <span className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-gray-400" />
                {location}
              </span>
            )}
            {parsed?.employment_type && (
              <span className="flex items-center gap-1.5">
                <Briefcase className="h-4 w-4 text-gray-400" />
                {formatLabel(parsed.employment_type)}
              </span>
            )}
            {parsed?.experience_level && (
              <span className="flex items-center gap-1.5">
                <Star className="h-4 w-4 text-gray-400" />
                {parsed.experience_level}
              </span>
            )}
            {salary && (
              <span className="flex items-center gap-1.5">
                <DollarSign className="h-4 w-4 text-gray-400" />
                {salary}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-gray-400" />
              Posted {formatRelativeDate(jd.created_at)}
            </span>
          </div>

          {(parsed?.location?.remote || parsed?.employment_type || parsed?.experience_level) && (
            <div className="flex flex-wrap gap-2 mt-4">
              {parsed?.location?.remote && (
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full px-3 py-1 text-xs font-medium">
                  Remote
                </span>
              )}
              {parsed?.employment_type && (
                <span className="bg-orange-50 text-orange-700 border border-orange-100 rounded-full px-3 py-1 text-xs font-medium">
                  {formatLabel(parsed.employment_type)}
                </span>
              )}
              {parsed?.experience_level && (
                <span className="bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-3 py-1 text-xs font-medium">
                  {parsed.experience_level}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left: job content */}
          <div className="lg:col-span-2 space-y-8">

            {parsed?.description && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <p className="text-gray-600 leading-relaxed">{parsed.description}</p>
              </div>
            )}

            {(parsed?.responsibilities?.length ?? 0) > 0 && (
              <Section title="Responsibilities" icon={<Briefcase className="h-4 w-4" />}>
                <CheckList items={parsed!.responsibilities!} />
              </Section>
            )}

            {(parsed?.qualifications?.length ?? 0) > 0 && (
              <Section title="Requirements" icon={<Star className="h-4 w-4" />}>
                <CheckList items={parsed!.qualifications!} />
              </Section>
            )}

            {skills.length > 0 && (
              <Section title="Skills" icon={<Building2 className="h-4 w-4" />}>
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill, i) => (
                    <span
                      key={i}
                      className="bg-slate-100 text-slate-700 rounded-lg px-3 py-1.5 text-sm font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {(parsed?.benefits?.length ?? 0) > 0 && (
              <Section title="Benefits" icon={<CheckCircle2 className="h-4 w-4" />}>
                <CheckList items={parsed!.benefits!} color="emerald" />
              </Section>
            )}

          </div>

          {/* Right: sticky apply card */}
          <div className="hidden lg:block">
            <div className="sticky top-20">
              <ApplyCard
                companyName={companyName}
                location={location}
                salary={salary}
                parsed={parsed}
                createdAt={jd.created_at}
                onApply={() => navigate(`/${workspaceCode}/jds/${jdId}/apply`)}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 pb-12">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-gray-900">More open positions</h2>
            <button
              onClick={() => navigate(`/${workspaceCode}`)}
              className="text-sm text-orange-500 hover:text-orange-600 font-medium flex items-center gap-1"
            >
              View all <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {suggestions.map((item) => (
              <SuggestionCard
                key={item.id}
                jd={item}
                onClick={() => navigate(`/${workspaceCode}/jds/${item.id}`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-100 text-center text-xs text-gray-400 py-6">
        Powered by AaaS
      </footer>
    </div>
  );
}

/* Sub-components */

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-orange-500">{icon}</span>
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function CheckList({ items, color = 'gray' }: { items: string[]; color?: 'gray' | 'emerald' }) {
  const dotClass =
    color === 'emerald'
      ? 'mt-1.5 h-2 w-2 rounded-full bg-emerald-400 flex-shrink-0'
      : 'mt-1.5 h-2 w-2 rounded-full bg-orange-400 flex-shrink-0';
  return (
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className={dotClass} />
          <span className="text-sm text-gray-600 leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

interface SuggestionCardProps {
  jd: PublicJDItem;
  onClick: () => void;
}

function SuggestionCard({ jd, onClick }: SuggestionCardProps) {
  const parsed = jd.parsed_data as ParsedJDData | null;
  const title = parsed?.title || jd.name;
  const location = getLocationText(parsed);

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-orange-200 transition-all group"
    >
      <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-600 transition-colors">
        {title}
      </h3>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {parsed?.employment_type && (
          <span className="bg-orange-50 text-orange-700 rounded-full px-2 py-0.5 text-xs font-medium">
            {formatLabel(parsed.employment_type)}
          </span>
        )}
        {parsed?.experience_level && (
          <span className="bg-blue-50 text-blue-700 rounded-full px-2 py-0.5 text-xs font-medium">
            {parsed.experience_level}
          </span>
        )}
      </div>
      {location && (
        <div className="flex items-center gap-1 text-xs text-gray-400 mb-3">
          <MapPin className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{location}</span>
        </div>
      )}
      <div className="flex items-center justify-between pt-3 border-t border-gray-50">
        <span className="text-xs text-gray-400">{formatRelativeDate(jd.created_at)}</span>
        <span className="text-xs font-medium text-orange-500 group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-0.5">
          View <ChevronRight className="h-3 w-3" />
        </span>
      </div>
    </div>
  );
}

interface ApplyCardProps {
  companyName?: string;
  location: string | null;
  salary: string | null;
  parsed: ParsedJDData | null;
  createdAt: string;
  onApply: () => void;
}

function ApplyCard({ companyName, location, salary, parsed, createdAt, onApply }: ApplyCardProps) {
  const skills = parsed?.skills_required ?? [];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-5 border-b border-gray-50">
        <button
          onClick={onApply}
          className="w-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-semibold py-3 rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
        >
          Apply for this position
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="p-5 space-y-3.5">
        {companyName && (
          <MetaRow icon={<Building2 className="h-4 w-4" />} label="Company" value={companyName} />
        )}
        {location && (
          <MetaRow icon={<MapPin className="h-4 w-4" />} label="Location" value={location} />
        )}
        {salary && (
          <MetaRow icon={<DollarSign className="h-4 w-4" />} label="Salary" value={salary} />
        )}
        {parsed?.employment_type && (
          <MetaRow
            icon={<Briefcase className="h-4 w-4" />}
            label="Employment"
            value={formatLabel(parsed.employment_type)}
          />
        )}
        {parsed?.experience_level && (
          <MetaRow
            icon={<Star className="h-4 w-4" />}
            label="Experience"
            value={parsed.experience_level}
          />
        )}
        <MetaRow
          icon={<Calendar className="h-4 w-4" />}
          label="Posted"
          value={formatRelativeDate(createdAt)}
        />
      </div>

      {skills.length > 0 && (
        <div className="px-5 pb-5 border-t border-gray-50 pt-4">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
            Required Skills
          </p>
          <div className="flex flex-wrap gap-1.5">
            {skills.map((skill, i) => (
              <span
                key={i}
                className="bg-slate-50 text-slate-600 border border-slate-100 rounded-lg px-2.5 py-1 text-xs font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetaRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-gray-300 mt-0.5 flex-shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-gray-400 mb-0.5">{label}</p>
        <p className="text-sm text-gray-700 font-medium truncate">{value}</p>
      </div>
    </div>
  );
}
